import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * The send log — every proposal and quotation that went out, who sent it, how,
 * and what happened to it since.
 */

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

const apiError = (err, fallback) => {
  const data = err?.response?.data;
  const firstFieldError = data?.errors?.[Object.keys(data?.errors || {})[0]]?.[0];
  return new Error(firstFieldError || data?.message || data?.error || fallback);
};

/**
 * @returns {{rows: Array, counts: object, pagination: object}}
 *          `counts` follows the filters, so the cards and the table always agree.
 */
export const fetchSends = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposals/sends`, {
      headers: jsonHeaders(),
      params,
    });
    const body = res.data || {};
    return {
      rows: Array.isArray(body.data) ? body.data : [],
      counts: body.counts || { proposal: 0, quotation: 0, total: 0 },
      pagination: body.pagination || { page: 1, limit: 20, total: 0, total_pages: 1 },
    };
  } catch (err) {
    throw apiError(err, "Failed to load the send log");
  }
};

/**
 * Send a document and record that it went.
 *
 * On `channel: "email"` the backend mails the `html` we hand it — the same
 * markup the rep previewed — so the client cannot receive a second, different
 * rendering. Every other channel only writes the row.
 *
 * `payload` carries the form inputs so the document can be rebuilt from the log.
 */
export const recordSend = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/proposals/sends`, payload, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "This send could not be recorded");
    }
    /* The API says what it actually did — "Email sent to client successfully."
       when it mailed the PDF, something else when it only logged the row. */
    return { data: body.data ?? null, message: body.message || "" };
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "This send could not be recorded");
  }
};

/**
 * The ways a client can pay, as the business currently accepts them.
 *
 * Read rather than listed here on purpose: a new method — a wallet, another
 * gateway — should appear in the dropdown the day accounts adds it, without a
 * frontend release. `code` is what the API wants back; `label` is what the rep
 * reads.
 */
export const fetchPaymentMethods = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/payment-methods`, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    const list = Array.isArray(body.data) ? body.data : Array.isArray(body) ? body : [];
    return list
      .map((m) => ({ value: m.code, label: m.label || m.name || m.code }))
      .filter((m) => m.value);
  } catch (err) {
    throw apiError(err, "Payment methods could not be loaded");
  }
};

/**
 * Money that arrived outside the payment link — a transfer, a cheque, cash.
 *
 * Nothing is activated by this. It is a claim by the rep who took the payment,
 * and it waits for accounts to verify it; a partial amount is allowed because
 * clients do pay in parts, and the remainder is tracked rather than refused.
 *
 * Sent as multipart so the proof travels with the claim — a receipt uploaded in
 * a second step is a receipt that can go missing between the two.
 */
export const recordOfflinePayment = async (id, fields) => {
  const body = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    body.append(key, value);
  });

  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposals/sends/${id}/record-payment`,
      body,
      { headers: { Authorization: jsonHeaders().Authorization } },
    );
    const data = res.data || {};
    if (data.status === 422 || data.status === false) {
      throw new Error(data.message || "This payment could not be recorded");
    }
    return { data: data.data ?? null, message: data.message || "" };
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "This payment could not be recorded");
  }
};

/* Verification is not called from here. Accounts approve or reject on their own
   screen, and those endpoints settle the send this payment belongs to along with
   the order — so a second way in would only be a second thing to keep in step. */

/** viewed / accepted / rejected / expired — whatever the recipient did next. */
export const updateSendStatus = async (id, status) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposals/sends/${id}/status`,
      { status },
      { headers: jsonHeaders() },
    );
    return { data: res.data?.data ?? null, message: res.data?.message || "" };
  } catch (err) {
    throw apiError(err, "The status could not be updated");
  }
};

/**
 * One send, with the inputs it was built from.
 *
 * The list does not carry `payload` — it would be dead weight on every row — so
 * reopening a document, or quoting the same client a second time, fetches it
 * here first.
 */
export const fetchSend = async (id) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposals/sends/${id}`, {
      headers: jsonHeaders(),
    });
    return res.data?.data || null;
  } catch (err) {
    throw apiError(err, "That send could not be opened");
  }
};
